import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setUnreadCount } from "system/store/slices/alert-slice";
import { infoIcon, urgentIcon, warningIcon, closeIcon, readIcon, filterIcon } from "../../assets/icons";
import { Button, ClickAwayListener, FormControl, FormControlLabel, Popper, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import useManageAlertApi from "system/hooks/use-api-manage-alert";
import { format } from "date-fns";
import { convertToHyperlinkedText } from "system/libraries/utilities";
import { sanitizeHtml } from "system/utils/sanitizeHtml";

interface Notification {
  title: string;
  type?: "urgent" | "warning" | "info" | "read";
  timestamp: string;
  details?: string;
  read?: boolean;
  alertRecipientId?: number;
  originalSeverity?: string;
}

const getIconByType = (type?: "urgent" | "warning" | "info" | "read") => {
  switch (type) {
    case "urgent":
      return urgentIcon;
    case "warning":
      return warningIcon;
    case "info":
      return infoIcon;
    case "read":
      return readIcon;
    default:
      return null;
  }
};

interface NotificationDrawerProps {
  visible: boolean;
  onClose: () => void;
}

const NotificationDrawer: React.FC<NotificationDrawerProps> = ({ visible, onClose }) => {
  const { getAlerts, isReadAlert, markAllReadAlerts } = useManageAlertApi();
  const dispatch = useDispatch();
  const [notificationList, setNotificationList] = useState<Notification[]>([]);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string>("");
  const [showFilterPanel, setShowFilterPanel] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const mapSeverityToType = (severity: string): Notification["type"] => {
    switch (severity) {
      case "Urgent":
        return "urgent";
      case "Warning":
        return "warning";
      case "Info":
        return "info";
      default:
        return undefined;
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await getAlerts();
      const data = response?.data ?? [];
      const unreadCount = response?.unreadCount ?? 0;

      const filtered = data.filter((item: any) => {
        const recipient = item.recipients?.[0];
        const isRead = recipient?.isRead || false;

        if (severityFilter === "Read") {
          return isRead;
        }

        if (!severityFilter) return true;

        return item.severityType?.toLowerCase() === severityFilter.toLowerCase();
      });

      const mapped: Notification[] = filtered
        .map((item: any) => {
          const recipient = item.recipients?.[0];
          const isRead = recipient?.isRead || false;
          return {
            title: item.subject,
            type: isRead ? "read" : mapSeverityToType(item.severityType),
            originalSeverity: item.severityType,
            timestamp: format(new Date(item.createdTimeStamp), "PPpp"),
            details: item.body || "",
            read: isRead,
            alertRecipientId: recipient?.alertRecipientId,
          };
        })
        .sort((a: Notification, b: Notification) => Number(a.read) - Number(b.read)); // Unread first

      setNotificationList(mapped);

      // Update selected notification if it exists
      if (selectedNotification?.alertRecipientId != null) {
        const updated = mapped.find((n) => n.alertRecipientId === selectedNotification.alertRecipientId);
        setSelectedNotification(updated ?? null);
      }

      dispatch(setUnreadCount(unreadCount));
    } catch (err) {
      console.error("Failed to fetch alerts:", err);
    }
  };

  useEffect(() => {
    setSeverityFilter("");
    fetchAlerts();
  }, [visible, getAlerts]);

  useEffect(() => {
    if (visible) {
      fetchAlerts();
    }
  }, [severityFilter]);

  const handleSelectNotification = (notif: Notification, index: number) => {
    setSelectedNotification(notif);
  };

  const handleDismissNotification = async (index: number) => {
    const notification = notificationList[index];

    if (notification?.alertRecipientId != null && !notification.read) {
      try {
        const res = await isReadAlert(notification.alertRecipientId, true);
        if (res === true) {
          await fetchAlerts();
        }
      } catch (err) {
        setNotificationList((prev) => prev.filter((_, i) => i !== index));
      }
    } else {
      setNotificationList((prev) => prev.filter((_, i) => i !== index));
    }
  };
  const handleDrawerClose = () => {
    setSelectedNotification(null);
    onClose();
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllReadAlerts();
      await fetchAlerts();
    } catch (err) {
      console.error("Error marking all as read:", err);
    }
  };

  if (!visible) return null;

  return (
    <div className="drawer-container">
      {selectedNotification && (
        <div className="notification-detail">
          <div className="detail-header">
            <span>{selectedNotification.title}</span>
            <button className="close-btn" onClick={() => setSelectedNotification(null)}>
              ×
            </button>
          </div>
          <div className="detail-body">
            <div className="badge-timestamp-row">
              {selectedNotification.type && (
                <span className={`badge ${selectedNotification.type}`}>
                  {selectedNotification.type.charAt(0).toUpperCase() + selectedNotification.type.slice(1)}
                </span>
              )}
              <span className="timestamp">{selectedNotification.timestamp}</span>
            </div>
            <div
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(convertToHyperlinkedText(selectedNotification.details || "")),
              }}
            />

            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                disabled={selectedNotification.type === "read"}
                sx={{
                  backgroundColor: selectedNotification.type === "read" ? "#F3F2F1" : "#3b82f6",
                  color: selectedNotification.type === "read" ? "#3B3A39" : "#ffffff",
                  fontSize: "14px",
                  padding: "6px 12px",
                  borderRadius: "4px",
                  cursor: selectedNotification.type === "read" ? "not-allowed" : "pointer",
                  "&:hover": {
                    backgroundColor: selectedNotification.type === "read" ? "#F3F2F1" : "#2563eb",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#8A8886",
                    color: "#3B3A39",
                  },
                }}
                onClick={() => setSelectedNotification(null)}
              >
                Remind Later
              </Button>

              <Button
                variant="outlined"
                onClick={async () => {
                  if (selectedNotification?.alertRecipientId != null) {
                    const newReadState = !selectedNotification.read;
                    try {
                      const res = await isReadAlert(selectedNotification.alertRecipientId, newReadState);
                      if (res === true) {
                        // Refresh the alerts to get the updated state from API
                        await fetchAlerts();
                      }
                    } catch (err) {
                      console.error("Failed to toggle read state:", err);
                    }
                  }
                }}
                sx={{
                  backgroundColor: selectedNotification?.read ? "#3B3A39" : "none",
                  borderRadius: "2px",
                  border: "1px solid",
                  fontSize: "14px",
                  padding: "6px 12px",
                  "&:hover": {
                    backgroundColor: "#2c2b2a",
                    cursor: "pointer",
                  },
                }}
              >
                {selectedNotification?.read ? "Mark as Unread" : "Mark as Read"}
              </Button>
            </Stack>
          </div>
        </div>
      )}

      <div className="notification-drawer">
        <div className="drawer-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Alert Notifications</span>
          <button className="close-btn" onClick={handleDrawerClose}>
            ×
          </button>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px" }}>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {/* Filter Button */}
            <Button
              variant="outlined"
              onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
              sx={{
                padding: "6px 20px",
                borderRadius: "2px",
                background: "#2f2f2f",
                border: "1px solid",
                fontSize: "14px",
                color: "#fff",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#2c2b2a",
                },
                display: "flex",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <img src={filterIcon} alt="Filter Icon" style={{ width: "16px", height: "16px" }} />
              Filter
            </Button>

            {/* Popper Dropdown */}
            <Popper open={open} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 1300 }}>
              <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                <FormControl
                  component="fieldset"
                  sx={{
                    background: "#2f2f2f",
                    padding: "12px",
                    borderRadius: "6px",
                    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
                    width: "200px",
                    marginTop: "6px",
                  }}
                >
                  <RadioGroup
                    value={severityFilter}
                    onChange={(e) => {
                      setSeverityFilter(e.target.value);
                      setAnchorEl(null);
                    }}
                    sx={{ color: "#fff" }}
                  >
                    <FormControlLabel value="Urgent" control={<Radio sx={{ color: "#fff" }} />} label="Urgent" />
                    <FormControlLabel value="Info" control={<Radio sx={{ color: "#fff" }} />} label="Info" />
                    <FormControlLabel value="Warning" control={<Radio sx={{ color: "#fff" }} />} label="Warning" />
                    <FormControlLabel value="Read" control={<Radio sx={{ color: "#fff" }} />} label="All Read Alerts" />
                  </RadioGroup>
                  <Typography
                    onClick={() => {
                      setSeverityFilter("");
                      setAnchorEl(null);
                    }}
                    sx={{
                      cursor: "pointer",
                      color: "#4F86F7",
                      fontSize: "13px",
                      paddingTop: "6px",
                      textDecoration: "underline",
                    }}
                  >
                    Clear Filter
                  </Typography>
                </FormControl>
              </ClickAwayListener>
            </Popper>
          </div>

          <Button
            variant="outlined"
            onClick={handleMarkAllAsRead}
            sx={{
              display: "flex",
              padding: "6px 20px",
              alignItems: "center",
              borderRadius: "2px",
              background: "#2f2f2f",
              border: "1px solid",
              fontSize: "14px",
              marginInline: "28px",
              "&:hover": {
                backgroundColor: "#2c2b2a",
                cursor: "pointer",
              },
            }}
          >
            Mark all as Read
          </Button>
        </div>

        <div className="drawer-body">
          {notificationList.length === 0 ? (
            <div className="no-alerts-message">No Alerts Found</div>
          ) : (
            notificationList.map((notif, index) => (
              <div
                key={index}
                className={`alert-card ${notif.type || ""} ${
                  selectedNotification?.alertRecipientId === notif.alertRecipientId ? "active" : ""
                }`}
                onClick={() => handleSelectNotification(notif, index)}
              >
                <div className="alert-title">
                  <div className="icon-title-group">
                    {getIconByType(notif.type) && (
                      <img src={getIconByType(notif.type)!} alt={`${notif.type} icon`} className="notification-icon" />
                    )}
                    <span
                      className="title-text"
                      style={{
                        fontWeight: notif.type === "read" ? "normal" : "bold",
                        display: "block",
                      }}
                    >
                      {notif.title}
                    </span>
                  </div>
                  {!notif.read && (
                    <button
                      className="card-close-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDismissNotification(index);
                      }}
                    >
                      <img src={closeIcon} alt="Close" className="close-icon-img" />
                    </button>
                  )}
                </div>

                {notif.type === "urgent" && notif.details && (
                  <p className="alert-details" title={notif.details.replace(/<[^>]+>/g, "") || ""}>
                    {notif.details.replace(/<[^>]+>/g, "") || ""}
                  </p>
                )}
                <div className="alert-meta">
                  {notif.originalSeverity && (
                    <span className={`badge ${mapSeverityToType(notif.originalSeverity)}`} style={{ marginRight: "8px" }}>
                      {notif.originalSeverity}
                    </span>
                  )}
                  <span>{notif.timestamp}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationDrawer;
